import { React, useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import LibraryService from "../../../service/library-service";
import AuthService from "../../../service/auth-service";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import Loading from "../../dialog/loading/loading";
import "./library-center-style.css";
import ConsultFloatButton from "../../custom/consult-float-button/consult-float-button";
import SiteFooter from "../../custom/site-footer/site-footer";
import PageSeo from "../../custom/page-seo/page-seo";
import useIsMobile from "../../../hooks/useIsMobile";

//timestamp → 「編寫於YYYY/MM/DD」（跟原本假資料格式一致）
const fmtWriteDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const p = (n) => String(n).padStart(2, "0");
  return `編寫於${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
};

//列表 shimmer 佔位卡片數量
const SKELETON_COUNT = 4;
//藏書列表 web 分頁：一頁 5 篇。API 走 skip/limit，前端自己算 totalPages
const ITEMS_PER_PAGE = 5;

//電腦版 shimmer 佔位卡：沿用 .article 容器，內容換成灰塊
const WebSkeletons = () =>
  Array.from({ length: SKELETON_COUNT }).map((_, i) => (
    <div className="article skeleton" key={`sk-${i}`}>
      <div className="content">
        <div className="sk-line sk-title" />
        <div className="sk-line sk-time" />
        <div className="sk-line sk-des" />
        <div className="sk-line sk-des short" />
      </div>
      <div className="nav-img-link">
        <div className="holder">
          <div className="sk-img" />
        </div>
      </div>
    </div>
  ));

//手機版 shimmer 佔位卡：沿用 .lm-article 容器
const MobileSkeletons = () =>
  Array.from({ length: SKELETON_COUNT }).map((_, i) => (
    <div className="lm-article skeleton" key={`sk-${i}`}>
      <div className="sk-line sk-title" />
      <div className="sk-line sk-time" />
      <div className="lm-art-body">
        <div className="sk-des" />
        <div className="sk-img" />
      </div>
    </div>
  ));

const LibraryCenterCompontent = ({
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  //完成註冊後要回到藏書中心（含目前 query 不重要、註冊回來重新拉就好）
  const registerFrom = location.pathname;
  const isMobile = useIsMobile();

  const [currentPage, setCurrentPage] = useState(1);
  //類別：存 categoryCode（"0" 工具書 / "1" 八字 / ...）；"" = 全部
  //  API 統一走 code、避免多國語系綁死中文名
  const [currentCategory, setCurrentCategory] = useState("");
  //0：精選（按讚多） 1：新到舊 2：我的收藏
  const [currentTab, setCurrentTab] = useState(0);
  //「我的收藏」tab 是否顯示：要求登入 + validCode === "2"
  const profile = AuthService.getUserProfile();
  const haveCollect =
    AuthService.getLoginStatus() && profile?.userValidCode === "2";
  //關鍵字
  const [keyword, setKeyword] = useState("");
  //搜尋模式
  const [searchMode, setSearchMode] = useState(false);
  //用於管理區間塊
  const sectionRefs = useRef([]);

  //—— API 狀態 ——
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  //列表 shimmer：每次抓取都開，用在切頁籤 / 搜尋 / 翻頁（初次進入走蓋房子 loading，不出 shimmer）
  const [listLoading, setListLoading] = useState(false);
  const [categories, setCategories] = useState([]); //類別選單從 API 拉

  //—— 互動狀態（哪些文章按過讚 / 收藏；Set 結構查找快）——
  const [likedSet, setLikedSet] = useState(() => new Set());
  const [collectedSet, setCollectedSet] = useState(() => new Set());
  //本地計數覆寫（避免每次操作都得重新整列 articles）
  //  key = articleID，value = { likes?, collects?, shares? }
  const [countOverrides, setCountOverrides] = useState({});
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  //避免短時間連點同一篇
  const [busyMap, setBusyMap] = useState(() => new Set());

  //—— 拉類別（mount 時一次）——
  useEffect(() => {
    LibraryService.getCategories()
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => setCategories([]));
  }, []);

  //蓋房子 loading 只在初次進入顯示；之後切頁籤 / 類別 / 搜尋 / 翻頁都不顯示
  const initialLoadRef = useRef(true);
  //shimmer 最少顯示時間（ms），避免 API 太快回來時一閃而過
  const SHIMMER_MIN_MS = 1000;
  //請求序號：快速連點時，只有最新一筆負責收掉 shimmer，舊的不提早關
  const reqIdRef = useRef(0);

  //—— 拉文章列表（page / category / tab / 搜尋變動時觸發）——
  //  tab 2（我的收藏）走 /me/collected；其他走 /articles
  //  類別篩選對 3 個 tab 都套用
  const fetchArticles = useCallback(() => {
    const myReq = ++reqIdRef.current;
    const isInitial = initialLoadRef.current;
    const startedAt = Date.now();
    //只有初次進入顯示蓋房子 loading（切換頁籤 / 搜尋時不再閃 loading）
    if (isInitial) setLoading(true);
    //後續抓取改用列表 shimmer
    setListLoading(true);
    //每次切換 filter 都重置本地計數覆寫（避免顯示過期狀態）
    setCountOverrides({});

    //skip/limit：web 一頁 5 篇；API 只回 total，totalPages 前端自己算
    const skip = (currentPage - 1) * ITEMS_PER_PAGE;
    const limit = ITEMS_PER_PAGE;

    let promise;
    if (currentTab === 2) {
      const params = { skip, limit };
      if (currentCategory) params.categoryCode = currentCategory;
      promise = LibraryService.getMyCollected(params);
    } else {
      const params = {
        skip,
        limit,
        sort: currentTab === 0 ? "hot" : "new",
      };
      if (currentCategory) params.categoryCode = currentCategory;
      if (searchMode && keyword.trim()) params.keyword = keyword.trim();
      promise = LibraryService.getArticles(params);
    }

    promise
      .then((res) => {
        const d = res.data?.data || {};
        const t = d.total || 0;
        setArticles(d.articles || []);
        setTotal(t);
        setTotalPages(Math.max(1, Math.ceil(t / ITEMS_PER_PAGE)));
      })
      .catch(() => {
        setArticles([]);
        setTotal(0);
        setTotalPages(1);
      })
      .finally(() => {
        //已有更新的請求 → 由最新那筆負責收尾，這筆不動
        if (myReq !== reqIdRef.current) return;
        initialLoadRef.current = false; //首次抓取完成後，後續都不再開 loading

        if (isInitial) {
          //初次走蓋房子 loading，shimmer 直接關（不延遲）
          setLoading(false);
          setListLoading(false);
          return;
        }
        //後續：shimmer 至少顯示 SHIMMER_MIN_MS，避免快速回應時一閃而過
        const rest = Math.max(0, SHIMMER_MIN_MS - (Date.now() - startedAt));
        setTimeout(() => {
          if (myReq === reqIdRef.current) setListLoading(false);
        }, rest);
      });
  }, [currentPage, currentCategory, currentTab, searchMode, keyword]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  //類別 / 搜尋切換時自動回到第 1 頁
  useEffect(() => {
    setCurrentPage(1);
  }, [currentCategory, currentTab, searchMode]);

  //—— articles 變動 → 批次查使用者對這些文章的狀態（按讚 / 收藏）——
  useEffect(() => {
    if (articles.length === 0) {
      setLikedSet(new Set());
      setCollectedSet(new Set());
      return;
    }
    const ids = articles.map((a) => a.articleID);
    LibraryService.getMyActions(ids)
      .then((res) => {
        const d = res.data?.data || {};
        setLikedSet(new Set(d.like || []));
        setCollectedSet(new Set(d.collect || []));
      })
      .catch(() => {
        setLikedSet(new Set());
        setCollectedSet(new Set());
      });
  }, [articles]);

  //—— 互動 helpers ——
  //  雙層判斷（依使用者要求順序：先看登入、再看註冊）：
  //    1. 沒登入 / profile 缺漏 → 跳登入 dialog
  //    2. 已登入但 validCode !== "2" → 跳「尚未完成註冊」確認 dialog
  //    3. validCode === "2" → 通過
  const ensureCanInteract = () => {
    const isLogin = AuthService.getLoginStatus();
    const p = AuthService.getUserProfile();
    if (!isLogin || !p) {
      setShowLoginDialog(true);
      return false;
    }
    if (p.userValidCode !== "2") {
      setShowRegisterPrompt(true);
      return false;
    }
    return true;
  };

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        }
      }
    } catch (e) {
      console.error("[LibraryList] firebase-login 失敗:", e);
    }
  };

  //取得文章目前顯示的計數（優先用本地 override、否則用 article 原值）
  const getCount = (a, field) =>
    countOverrides[a.articleID]?.[field] ?? (a[field] || 0);

  const setBusy = (articleID, val) => {
    setBusyMap((cur) => {
      const next = new Set(cur);
      if (val) next.add(articleID);
      else next.delete(articleID);
      return next;
    });
  };

  const handleLike = async (a) => {
    if (busyMap.has(a.articleID)) return;
    if (!ensureCanInteract()) return;

    const wasLiked = likedSet.has(a.articleID);
    const prevCount = getCount(a, "likes");

    //樂觀更新
    setLikedSet((cur) => {
      const next = new Set(cur);
      if (wasLiked) next.delete(a.articleID);
      else next.add(a.articleID);
      return next;
    });
    setCountOverrides((cur) => ({
      ...cur,
      [a.articleID]: {
        ...cur[a.articleID],
        likes: prevCount + (wasLiked ? -1 : 1),
      },
    }));
    setBusy(a.articleID, true);
    try {
      const res = await LibraryService.toggleLike(a.articleID);
      const d = res.data?.data;
      if (d) {
        setLikedSet((cur) => {
          const next = new Set(cur);
          if (d.active) next.add(a.articleID);
          else next.delete(a.articleID);
          return next;
        });
        setCountOverrides((cur) => ({
          ...cur,
          [a.articleID]: { ...cur[a.articleID], likes: d.count },
        }));
      }
    } catch (e) {
      //回滾
      setLikedSet((cur) => {
        const next = new Set(cur);
        if (wasLiked) next.add(a.articleID);
        else next.delete(a.articleID);
        return next;
      });
      setCountOverrides((cur) => ({
        ...cur,
        [a.articleID]: { ...cur[a.articleID], likes: prevCount },
      }));
      if (e?.response?.data?.needsLogin) setShowLoginDialog(true);
      else if (e?.response?.data?.needsRegister) setShowRegisterPrompt(true);
    } finally {
      setBusy(a.articleID, false);
    }
  };

  const handleCollect = async (a) => {
    if (busyMap.has(a.articleID)) return;
    if (!ensureCanInteract()) return;

    const wasCollected = collectedSet.has(a.articleID);
    const prevCount = getCount(a, "collects");

    setCollectedSet((cur) => {
      const next = new Set(cur);
      if (wasCollected) next.delete(a.articleID);
      else next.add(a.articleID);
      return next;
    });
    setCountOverrides((cur) => ({
      ...cur,
      [a.articleID]: {
        ...cur[a.articleID],
        collects: prevCount + (wasCollected ? -1 : 1),
      },
    }));
    setBusy(a.articleID, true);
    try {
      const res = await LibraryService.toggleCollect(a.articleID);
      const d = res.data?.data;
      if (d) {
        setCollectedSet((cur) => {
          const next = new Set(cur);
          if (d.active) next.add(a.articleID);
          else next.delete(a.articleID);
          return next;
        });
        setCountOverrides((cur) => ({
          ...cur,
          [a.articleID]: { ...cur[a.articleID], collects: d.count },
        }));
      }
    } catch (e) {
      setCollectedSet((cur) => {
        const next = new Set(cur);
        if (wasCollected) next.add(a.articleID);
        else next.delete(a.articleID);
        return next;
      });
      setCountOverrides((cur) => ({
        ...cur,
        [a.articleID]: { ...cur[a.articleID], collects: prevCount },
      }));
      if (e?.response?.data?.needsLogin) setShowLoginDialog(true);
      else if (e?.response?.data?.needsRegister) setShowRegisterPrompt(true);
    } finally {
      setBusy(a.articleID, false);
    }
  };

  //分享：手機 Web Share API / 桌面複製到剪貼簿
  //  剪貼簿格式：「標題\n連結」
  const handleShare = async (a) => {
    const shareURL = `${window.location.origin}/article/${a.articleID}`;
    const shareTitle = a.title || "元辰雲閣";
    const clipboardText = `${shareTitle}\n${shareURL}`;
    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareTitle,
            url: shareURL,
          });
        } catch {
          return;
        }
      } else {
        await navigator.clipboard?.writeText?.(clipboardText);
        alert("已複製標題與連結");
      }
      try {
        await LibraryService.share(a.articleID);
        setCountOverrides((cur) => ({
          ...cur,
          [a.articleID]: {
            ...cur[a.articleID],
            shares: getCount(a, "shares") + 1,
          },
        }));
      } catch {
        //計數失敗忽略
      }
    } catch {
      //share 全失敗也不擋
    }
  };

  const getPageBooks = (page) => {
    scrollTo(0);
  };

  //計算頁籤陣列
  const calcPages = (currentPage, total, windowSize = 3) => {
    if (!Number.isFinite(total) || total <= 0) return [];

    const safeTotal = Math.floor(total);
    const half = Math.floor(windowSize / 2);

    // 把 currentPage 夾在 1~total
    const p = Math.min(Math.max(1, Math.floor(currentPage)), safeTotal);

    // 先以「置中」估算
    let start = p - half;
    let end = p + half;

    // 左邊超出就往右補
    if (start < 1) {
      end += 1 - start;
      start = 1;
    }

    // 右邊超出就往左補
    if (end > safeTotal) {
      start -= end - safeTotal;
      end = safeTotal;
      if (start < 1) start = 1;
    }

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  //分頁完全由 API 控（total / totalPages 從 server 拿，articles 已是當前頁）
  const pagedArticles = articles;
  let pageArray = calcPages(currentPage, totalPages);

  const scrollTo = (key) => {
    sectionRefs.current[key]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // 用 ref 拿到最新狀態，避免 popstate 閉包拿到舊值
  const searchModeRef = useRef(searchMode);
  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  // 1) 監聽返回鍵：在搜尋模式時，返回鍵只關閉搜尋
  useEffect(() => {
    const onPopState = () => {
      if (searchModeRef.current) {
        // 返回鍵：先退出搜尋模式
        setSearchMode(false);
        setKeyword("");
        setCurrentCategory("");
        setCurrentTab(0);

        // 把歷史再塞回去，避免真的回上一頁
        window.history.pushState({ searchGuard: true }, "");
      } else {
        // 不攔截：讓瀏覽器正常回上一頁
        // 這裡不要 pushState
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // 2) 進入搜尋模式時，先 push 一個 state 讓「返回鍵」有得退
  useEffect(() => {
    if (searchMode) {
      setCurrentCategory(""); //搜尋模式不限制類別
      setCurrentTab(0);
      window.history.pushState({ searchGuard: true }, "");
    }
  }, [searchMode]);

  //categoryCode：類別代號（"0" 工具書 / "1" 八字 / ...）；"" 表示全部
  const selectCategory = (categoryCode) => {
    setCurrentCategory(categoryCode);
    setSearchMode(false);
    setKeyword("");
  };

  return (
    <div className="library-center-page">
      <PageSeo
        title="命理知識庫｜文章專欄"
        description="元辰雲閣命理知識庫：八字入門、十神術語、流年分析、戀愛合盤觀念、實用命理文章。看懂自己的命盤從這裡開始。"
        path="/library"
      />
      {/* 蓋房子動畫：只在初次進入顯示，切頁籤 / 搜尋 / 翻頁不再觸發 */}
      {loading && <Loading />}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
          ref={(el) => {
            sectionRefs.current[0] = el;
          }}
        >
          <div className="library-main">
            <div className="category">
              <div className="title">類別</div>
              <div className="cateline"></div>
              <ul>
                <li
                  className={currentCategory === "" ? "active" : ""}
                  onClick={() => selectCategory("")}
                >
                  全部
                </li>
                {categories.map((c) => (
                  <li
                    key={c.categoryID}
                    className={currentCategory === c.categoryCode ? "active" : ""}
                    onClick={() => selectCategory(c.categoryCode)}
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
            </div>

            <div className="library-articles">
              {!searchMode && (
                <div className="default-action">
                  <div className="divider"></div>
                  <div className="tabs">
                    <div
                      className="tab"
                      onClick={() => {
                        setCurrentTab(0);
                      }}
                    >
                      <div
                        className={`name ${currentTab == 0 ? "active-name" : ""}`}
                      >
                        精選藏書
                      </div>
                      <div
                        className={`tabline ${currentTab == 0 ? "active-tabline" : ""}`}
                      ></div>
                    </div>
                    <div
                      className="tab"
                      onClick={() => {
                        setCurrentTab(1);
                      }}
                    >
                      <div
                        className={`name ${currentTab == 1 ? "active-name" : ""}`}
                      >
                        由新到舊
                      </div>
                      <div
                        className={`tabline ${currentTab == 1 ? "active-tabline" : ""}`}
                      ></div>
                    </div>

                    {haveCollect && (
                      <div className="vertline">
                        <div className="inner-vert"></div>
                      </div>
                    )}

                    {haveCollect && (
                      <div
                        className="tab"
                        onClick={() => {
                          setCurrentTab(2);
                        }}
                      >
                        <div
                          className={`name ${currentTab == 2 ? "active-name" : ""}`}
                        >
                          我的收藏
                        </div>
                        <div
                          className={`tabline ${currentTab == 2 ? "active-tabline" : ""}`}
                        ></div>
                      </div>
                    )}
                  </div>

                  <div className="default-search">
                    <img src="../images/ic-search.png"></img>
                    <input
                      type="text"
                      maxLength={10}
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                          setSearchMode(true);
                        }
                      }}
                      placeholder="搜尋關鍵字"
                    />
                  </div>
                </div>
              )}

              {searchMode && (
                <div className="search-action">
                  <div className="search">
                    <img src="../images/ic-search.png"></img>
                    <input
                      type="text"
                      maxLength={10}
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                          setSearchMode(true);
                        }
                      }}
                      placeholder="搜尋關鍵字"
                    />
                  </div>
                  <span>搜尋結果</span>
                </div>
              )}

              <div className="article-list">
                {listLoading && !loading && <WebSkeletons />}
                {!listLoading && !loading && pagedArticles.length === 0 && (
                  <p className="empty-hint">尚無文章</p>
                )}
                {!listLoading &&
                  pagedArticles.map((a) => {
                  const writeDate = fmtWriteDate(a.publishedAt || a.createdAt);
                  return (
                    <div className="article" key={a.articleID}>
                      <div className="content">
                        <NavLink
                          className="nav-link"
                          to={`/article/${a.articleID}`}
                        >
                          <div className="title">{a.title}</div>
                          <div className="create-time">{writeDate}</div>
                          <div className="des">{a.summary}</div>
                        </NavLink>

                        <div className="actions">
                          <div className="action" onClick={() => handleLike(a)}>
                            <img
                              src={
                                likedSet.has(a.articleID)
                                  ? "../images/ic-have-like.png"
                                  : "../images/ic-default-like.png"
                              }
                              alt=""
                            />
                            <span>{getCount(a, "likes")}</span>
                          </div>
                          <div className="action" onClick={() => handleCollect(a)}>
                            <img
                              src={
                                collectedSet.has(a.articleID)
                                  ? "../images/ic-have-collect.png"
                                  : "../images/ic-default-collect.png"
                              }
                              alt=""
                            />
                            <span>{getCount(a, "collects")}</span>
                          </div>
                          <div className="action" onClick={() => handleShare(a)}>
                            <img src="../images/ic-default-share.png" alt="" />
                          </div>
                        </div>
                      </div>

                      <NavLink
                        className="nav-img-link"
                        to={`/article/${a.articleID}`}
                      >
                        <div className="holder">
                          {a.coverImage && (
                            <img src={a.coverImage} alt="" loading="lazy" />
                          )}
                        </div>
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className={`library-selector ${totalPages === 1 ? "one-page" : totalPages === 2 ? "two-page" : ""}`}
          >
            <img
              src={
                currentPage - 1 == 0
                  ? "../images/ic-non-select-left.png"
                  : "../images/ic-select-left.png"
              }
              onClick={() => {
                if (currentPage - 1 == 0) {
                  return;
                }
                setCurrentPage(currentPage - 1);
                getPageBooks(currentPage - 1);
              }}
            ></img>
            <div className="pages">
              {pageArray.map((number) => (
                <span
                  className={number == currentPage ? "current-page" : ""}
                  onClick={() => {
                    if (number == currentPage) {
                      return;
                    }

                    setCurrentPage(number);
                    getPageBooks(number);
                  }}
                >
                  {number}
                </span>
              ))}
            </div>
            <img
              src={
                currentPage == totalPages
                  ? "../images/ic-non-select-right.png"
                  : "../images/ic-select-right.png"
              }
              onClick={() => {
                if (currentPage == totalPages) {
                  return;
                }
                setCurrentPage(currentPage + 1);
                getPageBooks(currentPage + 1);
              }}
            ></img>
          </div>

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {isMobile && (
        <div
          className="library-mobile-main"
          ref={(el) => {
            sectionRefs.current[1] = el;
          }}
        >
          <div className="lm-content">
            {/* 類別 */}
            {!searchMode && (
              <div className="lm-category">
                <div className="lm-cat-title">類別</div>
                <div className="lm-cateline"></div>
                <ul className="lm-cat-list">
                  <li
                    className={currentCategory === "" ? "active" : ""}
                    onClick={() => selectCategory("")}
                  >
                    全部
                  </li>
                  {categories.map((c) => (
                    <li
                      key={c.categoryID}
                      className={currentCategory === c.categoryCode ? "active" : ""}
                      onClick={() => selectCategory(c.categoryCode)}
                    >
                      {c.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 搜尋列 */}
            {!searchMode && (
              <div className="lm-search-bar">
                <img src="../images/ic-search.png" />
                <input
                  type="text"
                  maxLength={10}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing)
                      setSearchMode(true);
                  }}
                  placeholder="搜尋關鍵字"
                />
              </div>
            )}

            {/* Tabs */}
            {!searchMode && (
              <div className="lm-tabs">
                <div className="lm-tabs-row">
                  {[
                    ["精選藏書", 0],
                    ["由新到舊", 1],
                  ].map(([label, idx]) => (
                    <div
                      key={idx}
                      className={`lm-tab ${currentTab === idx ? "active" : ""}`}
                      onClick={() => setCurrentTab(idx)}
                    >
                      {label}
                    </div>
                  ))}
                  {haveCollect && <div className="lm-vertline"></div>}
                  {haveCollect && (
                    <div
                      className={`lm-tab ${currentTab === 2 ? "active" : ""}`}
                      onClick={() => setCurrentTab(2)}
                    >
                      我的收藏
                    </div>
                  )}
                </div>
                <div className="lm-tab-underline"></div>
              </div>
            )}

            {/* 搜尋模式 */}
            {searchMode && (
              <div className="lm-search-mode">
                <div className="lm-search-full">
                  <img src="../images/ic-search.png" />
                  <input
                    type="text"
                    maxLength={10}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.nativeEvent.isComposing)
                        setSearchMode(true);
                    }}
                    placeholder="搜尋關鍵字"
                  />
                </div>
                <span className="lm-search-result">搜尋結果</span>
              </div>
            )}

            {/* 文章列表 */}
            <div className="lm-article-list">
              {listLoading && !loading && <MobileSkeletons />}
              {!listLoading && !loading && pagedArticles.length === 0 && (
                <p className="empty-hint">尚無文章</p>
              )}
              {!listLoading &&
                pagedArticles.map((a) => {
                const writeDate = fmtWriteDate(a.publishedAt || a.createdAt);
                return (
                  <div className="lm-article" key={a.articleID}>
                    <NavLink
                      className="nav-link"
                      to={`/article/${a.articleID}`}
                    >
                      <div className="lm-art-title">{a.title}</div>
                      <div className="lm-art-time">{writeDate}</div>
                      <div className="lm-art-body">
                        <div className="lm-art-des">{a.summary}</div>
                        {a.coverImage && (
                          <img
                            className="lm-art-img"
                            src={a.coverImage}
                            alt=""
                            loading="lazy"
                          />
                        )}
                      </div>
                    </NavLink>
                    <div className="lm-actions">
                      <div className="lm-action" onClick={() => handleLike(a)}>
                        <img
                          src={
                            likedSet.has(a.articleID)
                              ? "../images/ic-have-like.png"
                              : "../images/ic-default-like.png"
                          }
                          alt=""
                        />
                        <span>{getCount(a, "likes")}</span>
                      </div>
                      <div className="lm-action" onClick={() => handleCollect(a)}>
                        <img
                          src={
                            collectedSet.has(a.articleID)
                              ? "../images/ic-have-collect.png"
                              : "../images/ic-default-collect.png"
                          }
                          alt=""
                        />
                        <span>{getCount(a, "collects")}</span>
                      </div>
                      <div className="lm-action" onClick={() => handleShare(a)}>
                        <img src="../images/ic-default-share.png" alt="" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 分頁器 */}
            <div
              className={`lm-selector ${totalPages === 1 ? "one-page" : totalPages === 2 ? "two-page" : ""}`}
            >
              <img
                src={
                  currentPage - 1 === 0
                    ? "../images/ic-non-select-left.png"
                    : "../images/ic-select-left.png"
                }
                onClick={() => {
                  if (currentPage - 1 !== 0) {
                    setCurrentPage(currentPage - 1);
                    scrollTo(1);
                  }
                }}
              />
              <div className="lm-pages">
                {pageArray.map((number) => (
                  <span
                    key={number}
                    className={number === currentPage ? "current-page" : ""}
                    onClick={() => {
                      if (number !== currentPage) {
                        setCurrentPage(number);
                        scrollTo(1);
                      }
                    }}
                  >
                    {number}
                  </span>
                ))}
              </div>
              <img
                src={
                  currentPage === totalPages
                    ? "../images/ic-non-select-right.png"
                    : "../images/ic-select-right.png"
                }
                onClick={() => {
                  if (currentPage !== totalPages) {
                    setCurrentPage(currentPage + 1);
                    scrollTo(1);
                  }
                }}
              />
            </div>
          </div>

          <SiteFooter />
        </div>
      )}

      <ConsultFloatButton />

      {/* 按讚 / 收藏未登入時跳出；關掉 → 留在當前頁 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="按讚 / 收藏需要完成註冊才能使用，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default LibraryCenterCompontent;
